/**
 * NotificationBell — top-bar bell icon with unread badge + dropdown of notifications.
 * Polls /api/portal/notifications every 30s.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Sparkles, Receipt, AlertTriangle, Mail, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authClient } from '../../services/onboarding';

const ICONS = { sparkles: Sparkles, receipt: Receipt, warning: AlertTriangle, mail: Mail, bell: Bell };
const LEVEL_COLORS = {
  info:     { bg: 'rgba(46,112,173,0.15)',  fg: '#6BA6F5' },
  success:  { bg: 'rgba(16,185,129,0.15)',  fg: '#10B981' },
  warning:  { bg: 'rgba(242,180,30,0.15)',  fg: '#F2B41E' },
  critical: { bg: 'rgba(239,68,68,0.15)',   fg: '#EF4444' },
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await authClient.get('/notifications', { params: { limit: 30 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    const onClick = (ev) => {
      if (!ref.current?.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => { clearInterval(id); document.removeEventListener('mousedown', onClick); };
  }, []);

  const markAllRead = async () => {
    await authClient.post('/notifications/mark-read', { all: true });
    load();
  };

  const handleClick = async (n) => {
    await authClient.post('/notifications/mark-read', { ids: [n.id] });
    if (n.link) navigate(n.link);
    setOpen(false);
    load();
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await authClient.delete(`/notifications/${id}`);
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-8 h-8 rounded-lg relative flex items-center justify-center text-nexus-text-tertiary hover:text-nexus-text-primary transition-colors"
        style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)' }}
        data-testid="topbar-notifications"
        title="Notifications"
        aria-label="Notifications">
        <Bell size={14} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full text-[9px] font-bold flex items-center justify-center px-1"
            style={{ background: '#F26B1F', color: '#fff', boxShadow: '0 0 0 2px var(--layer-1)' }}
            data-testid="notif-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 mt-2 w-[380px] rounded-xl shadow-2xl z-50 max-h-[70vh] overflow-hidden flex flex-col"
            style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
            data-testid="notif-dropdown">
            <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: 'var(--hairline)' }}>
              <div>
                <div className="text-[13px] font-semibold text-nexus-text-primary">Notifications</div>
                <div className="text-[11px] text-nexus-text-tertiary">{unread} unread</div>
              </div>
              {unread > 0 && (
                <button onClick={markAllRead}
                  className="text-[11px] flex items-center gap-1 text-nexus-text-secondary hover:text-nexus-text-primary"
                  data-testid="mark-all-read">
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
            </div>

            <div className="overflow-y-auto flex-1">
              {items.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <Bell size={28} className="mx-auto text-nexus-text-tertiary opacity-50 mb-3" />
                  <p className="text-[12px] text-nexus-text-tertiary">No notifications yet</p>
                  <p className="text-[10px] text-nexus-text-tertiary opacity-70 mt-1">
                    You'll see purchase receipts, low-balance alerts and team updates here.
                  </p>
                </div>
              ) : (
                items.map((n) => {
                  const Icon = ICONS[n.icon] || Bell;
                  const tone = LEVEL_COLORS[n.level] || LEVEL_COLORS.info;
                  return (
                    <button key={n.id} onClick={() => handleClick(n)}
                      className="w-full px-4 py-3 flex items-start gap-3 hover:bg-white/5 text-left transition-colors border-b group"
                      style={{ borderColor: 'var(--hairline)', background: n.read ? 'transparent' : 'rgba(242,107,31,0.04)' }}
                      data-testid={`notif-item-${n.id}`}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: tone.bg, color: tone.fg }}>
                        <Icon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-[12.5px] font-medium text-nexus-text-primary truncate">{n.title}</p>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#F26B1F' }} />}
                        </div>
                        <p className="text-[11.5px] text-nexus-text-secondary leading-snug mt-0.5 line-clamp-2">{n.body}</p>
                        <p className="text-[10px] text-nexus-text-tertiary mt-1">{fmtTime(n.created_at)}</p>
                      </div>
                      <button onClick={(e) => handleDelete(e, n.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-nexus-text-tertiary hover:text-nexus-text-primary"
                        title="Dismiss" aria-label="Dismiss">
                        <X size={12} />
                      </button>
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
